import type {InjectionKey} from 'vue';
import {inject, provide} from 'vue';

/**
 * Type-safe provide/inject helper. Returns `[injectContext, provideContext]`.
 * Per Vue-Konvention (siehe webapp/src/VUE_RULES.md) ist dies der **einzige** erlaubte Weg, um
 * provide/inject zu nutzen – nie `inject`/`provide` direkt aufrufen.
 */
export function createContext<ContextValue>(
    providerComponentName: string | string[],
    contextName: string = 'default',
) {
    const symbolDescription =
        typeof providerComponentName === 'string' && !contextName
            ? `${providerComponentName}Context`
            : contextName;

    const injectionKey: InjectionKey<ContextValue | null> = Symbol(symbolDescription);

    const injectContext: <T extends ContextValue | null | undefined = ContextValue>(fallback?: T) => T extends null ? (ContextValue | null) : ContextValue = <T extends ContextValue | null | undefined = ContextValue>(
        fallback?: T,
    ): T extends null ? ContextValue | null : ContextValue => {
        const context = inject(injectionKey, fallback);
        if (context) return context as T extends null ? ContextValue | null : ContextValue;
        if (context === null) return context as T extends null ? ContextValue | null : ContextValue;

        throw new Error(
            `Injection \`${injectionKey.toString()}\` not found. Component must be used within ` +
            (Array.isArray(providerComponentName)
                ? `one of: ${providerComponentName.join(', ')}`
                : `\`${providerComponentName}\``),
        );
    };

    const provideContext: (contextValue: ContextValue) => ContextValue = (contextValue: ContextValue) => {
        provide(injectionKey, contextValue);
        return contextValue;
    };

    return [injectContext, provideContext] as const;
}
